import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";
import Svg, { Circle, Line } from "react-native-svg";

import { useAuth } from "../../../lib/authContext";
import { EmptyState } from "../../../components/EmptyState";
import { listMyJudgeSubmissions, type JudgeSubmissionQueueRow } from "../../../lib/judges";
import { FONTS } from "../../../lib/brand";
import { LogoMark } from "../../../components/brand/Logo";
import { PANE_THEMES } from "../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Judge;
const FOREST_MID = THEME.surfaceBg;
const AMBER = THEME.accent;
const SAGE = THEME.success;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = THEME.destructive;

const PAGE_MAX_W = 1240;
const DUE_SOON_DAYS = 7;

function isPastDue(d: Date, now: Date): boolean {
  return d.getTime() <= now.getTime();
}

function daysUntil(d: Date, now: Date): number {
  const ms = d.getTime() - now.getTime();
  return ms / 86_400_000;
}

function fmtDueChip(due_at: string | null): string {
  if (!due_at) return "No due date";
  const d = new Date(due_at);
  const now = new Date();
  const days = daysUntil(d, now);
  if (isPastDue(d, now)) return `Overdue · ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  if (days < 1) return `Due today`;
  if (days < 2) return `Due tomorrow`;
  if (days < 7) return `Due in ${Math.ceil(days)} days`;
  return `Due ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

function firstName(full: string | null | undefined): string {
  if (!full?.trim()) return "judge";
  return full.trim().split(/\s+/)[0];
}

export default function JudgeAssignmentsScreen() {
  const { user } = useAuth();
  const [rows, setRows] = useState<JudgeSubmissionQueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [completedOpen, setCompletedOpen] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const data = await listMyJudgeSubmissions();
      setRows(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load assignments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const name = typeof meta.full_name === "string" ? meta.full_name : null;

  const grouped = useMemo(() => {
    const now = new Date();
    const sevenDaysOut = now.getTime() + DUE_SOON_DAYS * 86_400_000;

    const dueSoon: JudgeSubmissionQueueRow[] = [];
    const all: JudgeSubmissionQueueRow[] = [];
    const completed: JudgeSubmissionQueueRow[] = [];

    for (const r of rows) {
      const isCompleted = r.my_score_status === "submitted";
      if (isCompleted) {
        completed.push(r);
        continue;
      }
      const due = r.deliverable_due_at ? new Date(r.deliverable_due_at) : null;
      if (due && due.getTime() <= sevenDaysOut) {
        dueSoon.push(r);
      } else {
        all.push(r);
      }
    }

    dueSoon.sort((a, b) => {
      const da = a.deliverable_due_at ? new Date(a.deliverable_due_at).getTime() : Infinity;
      const db = b.deliverable_due_at ? new Date(b.deliverable_due_at).getTime() : Infinity;
      return da - db;
    });
    all.sort((a, b) => a.competition_name.localeCompare(b.competition_name) || a.deliverable_sequence - b.deliverable_sequence);
    completed.sort((a, b) => {
      const ua = a.my_score_updated_at ?? "";
      const ub = b.my_score_updated_at ?? "";
      return ub.localeCompare(ua);
    });

    return { dueSoon, all, completed };
  }, [rows]);

  const stats = useMemo(() => {
    const now = Date.now();
    const sevenDaysOut = now + DUE_SOON_DAYS * 86_400_000;
    let awaiting = 0;
    let dueThisWeek = 0;
    let completed = 0;
    const programs = new Set<string>();
    for (const r of rows) {
      programs.add(r.competition_id);
      if (r.my_score_status === "submitted") {
        completed += 1;
        continue;
      }
      awaiting += 1;
      const due = r.deliverable_due_at ? new Date(r.deliverable_due_at).getTime() : null;
      if (due != null && due <= sevenDaysOut) dueThisWeek += 1;
    }
    return { awaiting, dueThisWeek, completed, programs: programs.size };
  }, [rows]);

  const sublineActive =
    stats.awaiting === 0
      ? "No assignments waiting. We'll email you when something needs your review."
      : `${stats.awaiting} submission${stats.awaiting === 1 ? "" : "s"} waiting on your review · ${stats.dueThisWeek} due this week`;

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: PAGE_MAX_W }}>
        {/* Hero */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Judge Portal
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.displayLight,
            fontSize: 30,
            lineHeight: 36,
            color: LINEN,
          }}
        >
          Welcome back, <Text style={{ fontStyle: "italic", color: AMBER }}>{firstName(name)}</Text>.
        </Text>
        <Text
          style={{
            marginTop: 4,
            fontFamily: FONTS.bodyLight,
            fontSize: 13,
            color: WM,
          }}
        >
          {sublineActive}
        </Text>

        {/* Error banner */}
        {err ? (
          <View
            style={{
              marginTop: 18,
              paddingHorizontal: 14,
              paddingVertical: 10,
              borderWidth: 1,
              borderColor: "rgba(220,80,80,0.40)",
              backgroundColor: "rgba(220,80,80,0.08)",
              borderRadius: 4,
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: RED }}>{err}</Text>
          </View>
        ) : null}

        {/* Stats strip */}
        <View
          style={{
            marginTop: 22,
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <StatTile label="Awaiting your review" value={stats.awaiting} accent={AMBER} />
          <StatTile label="Due this week" value={stats.dueThisWeek} accent={stats.dueThisWeek > 0 ? AMBER : WM} />
          <StatTile label="Completed" value={stats.completed} accent={SAGE} />
          <StatTile label="Programs" value={stats.programs} accent={LINEN} />
        </View>

        {loading ? (
          <View style={{ marginTop: 28, gap: 10 }}>
            {[0, 1, 2, 3].map((i) => (
              <View
                key={i}
                style={{
                  height: 80,
                  borderRadius: 6,
                  backgroundColor: "rgba(255,255,255,0.04)",
                  borderWidth: 1,
                  borderColor: BORDER,
                }}
              />
            ))}
          </View>
        ) : rows.length === 0 ? (
          <View style={{ marginTop: 32 }}>
            <EmptyState
              eyebrow="No assignments"
              title="Nothing waiting on your review."
              body="When a client invites you to judge a program, the assignments will appear here."
            />
          </View>
        ) : (
          <View style={{ marginTop: 28 }}>
            {grouped.dueSoon.length > 0 ? (
              <Section title="Due soon">
                {grouped.dueSoon.map((r) => (
                  <AssignmentRow key={r.submission_id} row={r} />
                ))}
              </Section>
            ) : null}

            {grouped.all.length > 0 ? (
              <Section title="All assignments">
                {grouped.all.map((r) => (
                  <AssignmentRow key={r.submission_id} row={r} />
                ))}
              </Section>
            ) : null}

            {grouped.completed.length > 0 ? (
              <View style={{ marginTop: 22 }}>
                <Pressable
                  onPress={() => setCompletedOpen((o) => !o)}
                  style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 9.5,
                      letterSpacing: 2,
                      textTransform: "uppercase",
                      color: SAGE,
                    }}
                  >
                    {completedOpen ? "Hide completed ▴" : `Completed (${grouped.completed.length}) ▾`}
                  </Text>
                </Pressable>
                {completedOpen ? (
                  <View style={{ marginTop: 10, gap: 8 }}>
                    {grouped.completed.map((r) => (
                      <AssignmentRow key={r.submission_id} row={r} />
                    ))}
                  </View>
                ) : null}
              </View>
            ) : null}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: string;
}) {
  return (
    <View
      style={{
        flexBasis: 160,
        flexGrow: 1,
        flexShrink: 1,
        padding: 16,
        borderRadius: 6,
        backgroundColor: FOREST_MID,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1.8,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          marginTop: 8,
          fontFamily: FONTS.displayMedium,
          fontSize: 30,
          color: accent,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={{ marginBottom: 22 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 10,
        }}
      >
        {title}
      </Text>
      <View style={{ gap: 8 }}>{children}</View>
    </View>
  );
}

function AssignmentRow({ row }: { row: JudgeSubmissionQueueRow }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null;
  const [hover, setHover] = useState(false);

  const completed = row.my_score_status === "submitted";
  const draft = row.my_score_status === "draft";

  const due = row.deliverable_due_at ? new Date(row.deliverable_due_at) : null;
  const overdue = due ? isPastDue(due, new Date()) && !completed : false;

  const dueLabel = fmtDueChip(row.deliverable_due_at);

  const onClick = () =>
    router.push(`/judge/competition/${row.competition_id}/score/${row.submission_id}` as Href);

  return (
    <Pressable
      onPress={onClick}
      {...(Platform.OS === "web"
        ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
        : {})}
      style={[
        {
          minHeight: 80,
          paddingHorizontal: 16,
          paddingVertical: 12,
          borderRadius: 6,
          backgroundColor: hover ? "rgba(181,135,58,0.06)" : FOREST_MID,
          borderWidth: 1,
          borderColor: hover ? "rgba(181,135,58,0.30)" : BORDER,
          flexDirection: "row",
          alignItems: "center",
          gap: 14,
        } as ViewStyle,
        cursorWeb,
        Platform.OS === "web"
          ? ({ transition: "background-color 140ms, border-color 140ms" } as object)
          : null,
      ]}
    >
      {/* Left — program / company */}
      <View style={{ flex: 1.3, minWidth: 0 }}>
        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 16,
            color: LINEN,
          }}
          numberOfLines={1}
        >
          {row.competition_name}
        </Text>
        <Text
          style={{
            marginTop: 2,
            fontFamily: FONTS.body,
            fontSize: 11,
            color: WS,
          }}
          numberOfLines={1}
        >
          {row.company_name}
        </Text>
      </View>

      {/* Middle — deliverable + team */}
      <View style={{ flex: 1.6, minWidth: 0 }}>
        <Text
          style={{
            fontFamily: FONTS.bodyMedium,
            fontSize: 12,
            color: W,
          }}
          numberOfLines={1}
        >
          {row.deliverable_sequence}. {row.deliverable_name}
        </Text>
        <Text
          style={{
            marginTop: 2,
            fontFamily: FONTS.body,
            fontSize: 12,
            color: WM,
          }}
          numberOfLines={1}
        >
          {row.team_name}
        </Text>
      </View>

      {/* Right — pills */}
      <View
        style={{
          flexShrink: 0,
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
        }}
      >
        <DueChip label={dueLabel} overdue={overdue} muted={completed} />
        <StatusPill completed={completed} draft={draft} />
        {!completed ? (
          <View
            style={{
              paddingHorizontal: 12,
              paddingVertical: 7,
              borderRadius: 999,
              backgroundColor: AMBER,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: THEME.accentText,
              }}
            >
              {"Score →"}
            </Text>
          </View>
        ) : (
          <View
            style={{
              paddingHorizontal: 12,
              paddingVertical: 7,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: SAGE,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10.5,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: SAGE,
              }}
            >
              View score
            </Text>
          </View>
        )}
      </View>
    </Pressable>
  );
}

function DueChip({
  label,
  overdue,
  muted,
}: {
  label: string;
  overdue: boolean;
  muted: boolean;
}) {
  const color = muted ? WL : overdue ? RED : WS;
  return (
    <View
      style={{
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: muted ? BORDER : overdue ? "rgba(230,154,138,0.4)" : "rgba(255,255,255,0.16)",
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 10.5,
          color,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function StatusPill({ completed, draft }: { completed: boolean; draft: boolean }) {
  let label: string;
  let color: string;
  if (completed) {
    label = "Submitted";
    color = SAGE;
  } else if (draft) {
    label = "Draft";
    color = AMBER;
  } else {
    label = "Awaiting";
    color = WM;
  }
  return (
    <View
      style={{
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.04)",
        borderWidth: 1,
        borderColor: color,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function CoravuMarkFaint({ size }: { size: number }) {
  return (
    <LogoMark size={size} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} style={{ opacity: 0.18 }} />
  );
}
